import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InitialConfigService } from '@app/core/app-config/initial-config.service';
import { CMembership, IMembershipUpgradesInfo } from '@app/tickets/tickets.model';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { IBookingRenewed } from './product-memberships.model';
import { IOrderItem } from '@core/order/models/order-item.model';
import { EMessageStatus, IMessage } from '@core/toast/model/message.model';
import { MessageService } from '@core/toast/service/message.service';

@Injectable({
  providedIn: 'root'
})
export class ProductMembershipsService {
  private availableUpgrades$ = new BehaviorSubject<IMembershipUpgradesInfo[] | undefined>(undefined);

  constructor(
    private http: HttpClient,
    private initialConfigService: InitialConfigService,
    private messageService: MessageService
  ) {}

  /**
   * Gets the current subject containing the availableUpgrades. Only emits when it has value.
   * @returns Observable of the availableUpgrades items.
   */
  getAvailableUpgrades(): Observable<IMembershipUpgradesInfo[] | undefined> {
    return this.availableUpgrades$.asObservable();
  }

  /**
   * Sets the subject with the availableUpgrades value.
   * @param value object or array of IMembershipUpgradesInfo[]
   */
  setAvailableUpgrades(value: IMembershipUpgradesInfo[]): void {
    this.availableUpgrades$.next(value);
  }

  /** Checks if membership was selected to upgrade but wasn't modified. */
  isUnmodifiedMembership(orderItem: IOrderItem): boolean {
    return orderItem.manageBookingType === CMembership && !orderItem.isManageItemModified;
  }

  /** Calls the API to check if a booking has ever been renewed. Display a message if it has already been renewed */
  async getIsBookingRenewedWithWarning(bookingUniqueId: string): Promise<boolean> {
    const response: IBookingRenewed = await firstValueFrom(
      this.http.get<IBookingRenewed>(`${this.initialConfigService.getBaseApiUrl()}/bookings/${bookingUniqueId}/renewed`)
    );

    const isRenewed = response.isRenewed;
    if (isRenewed) {
      this.showRenewedMembershipMessage();
    }

    return isRenewed;
  }

  private showRenewedMembershipMessage() {
    const message: IMessage = {
      severity: EMessageStatus.Success,
      detail: 'MEMBERSHIP.YOU_HAVE_ALREADY_RENEWED_YOUR_MEMBERSHIP'
    };

    this.messageService.add(message);
  }
}
